import React, { useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import useNotificationsUnreadCount from '../hooks/useNotificationsUnreadCount';
import usePaginatedNotifications from '../hooks/usePaginatedNotifications';

import NotificationsNavDropdownMenu, { DROPDOWN_MENU_DOM_ID } from './NotificationsNavDropdownMenu';

const axiosClient = axios.create({
	withCredentials: true,
});

const propTypes = {

	/**
	 * The current user.
	 */
	currentUser: PropTypes.shape({

		/**
		 * The user's ID.
		 */
		id: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The organization for the current user.
	 */
	myOrganization: PropTypes.shape({

		/**
		 * The organization ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The organization title.
		 */
		title: PropTypes.string.isRequired,

	}).isRequired,

};

const NotificationsNavDropdown = ({ currentUser, myOrganization }) => {

	const [unreadCount, setUnreadCount] = useNotificationsUnreadCount({
		user_id: currentUser.id,
		organization_id: myOrganization.id,
	});

	const [
		notifications,
		setNotifications,
		showError,
		loadState,
		fetchNotifications
	] = usePaginatedNotifications({
		user_id: currentUser.id,
		organization_id: myOrganization.id,
	}, DROPDOWN_MENU_DOM_ID);

	const [markingRead, setMarkingRead] = useState(false);

	/**
	 * Marks a notification as read by the user.
	 *
	 * @param {String} notificationID  The id of the notification to mark as read.
	 */
	const handleMarkRead = (notificationID) => {

		const clickedNotification =
			notifications.find((notification) => notification.id === notificationID);

		if (!clickedNotification.read && !markingRead) {

			setMarkingRead(true);

			axiosClient.patch(`/api/v2/notifications/${notificationID}`, {
				read: true,
			}).then((response) => {

				if (response.data) {

					// Update state with the patched notification
					setNotifications((prev) => {

						const result = [...prev];

						const notificationIdx = prev
							.findIndex((notification) => notification.id === response.data.id);

						if (notificationIdx >= 0) {
							result[notificationIdx] = {
								...result[notificationIdx],
								...response.data,
							};
						}

						return result;

					});

					// Fetch the latest unread count
					axiosClient.get(`/api/v2/notifications/unread-count/${currentUser.id}`)
						.then(({ data }) => {

							setUnreadCount(data);
							setMarkingRead(false);

						}).catch((err) => {

							console.error(err);

						});

				}

			});

		}

	};

	/**
	 * Marks all notifications as read by the user.
	 */
	const handleMarkAllRead = () => {

		axiosClient.post('/api/v2/notifications/mark-read').then((response) => {

			if (response.data) {

				// Update all notifications in state as read
				setNotifications((prev) => (
					prev.map((notification) => ({
						...notification,
						read: true,
					}))
				));

				// Set unread count to 0
				setUnreadCount(() => ({ count: 0 }));

			}

		});

	};

	return (
		<NotificationsNavDropdownMenu
			notifications={notifications}
			unreadCount={unreadCount ? unreadCount.count : 0}
			showError={showError}
			menuLoadState={loadState}
			onMarkRead={handleMarkRead}
			onMarkAllRead={handleMarkAllRead}
			onLoadMenuData={fetchNotifications} />
	);

};

NotificationsNavDropdown.propTypes = propTypes;

export default NotificationsNavDropdown;
